import React from 'react';
import {
  createStyles,
  makeStyles,
  Theme,
  Typography,
  Box,
  IconButton,
} from '@material-ui/core';
import OutageCountCircleIcon from './OutageCircleIcon';
import HomeIcon from '../../../../../static/homeIconOutageHeader.svg';
import { useTranslation } from '../../../../../hooks/useTranslation';
import colors from '../../../../../themes/main-colors';
import CloseIcon from '@material-ui/icons/Close';
import clsx from 'clsx';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    container: {
      display: 'flex',
      flexDirection: 'row',
      justifyContent: 'flex-start',
      alignItems: 'center',
      position: 'relative',
      marginBottom: '3px',
      '& > *:nth-child(2)': {
        paddingLeft: '10px',
        whiteSpace: 'nowrap',
      },
    },
    homeIcon: {
      height: '37px',
      width: '37px',
    },
    isOut: {
      fill: colors.errorRed,
      '& svg': {
        height: '37px',
        width: '37px',
      },
      position: 'relative',
      top: '1px',
      left: '3px',
    },
    isNotOut: {
      fill: colors.sparkBlue,
      '& svg': {
        height: '37px',
        width: '37px',
      },
    },
    noOutagePin: {
      '& path': {
        fill: colors.orange,
      },
    },
    noOutageHome: {
      fill: colors.sparkBlue,
      position: 'relative',
      top: '1px',
      left: '3px',
      '& svg': {
        height: '37px',
        width: '37px',
      },
    },
    closeIcon: {
      height: '100%',
      width: '100%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'flex-end',
    },
    closeIconBtn: {
      padding: 2,
    },
    centerAlign: {
      justifyContent: 'center',
      borderBottom: `2px solid ${colors.lightGray3}`,
      paddingBottom: 10,
    },
    outageLeftPanelHeader: {
      fontSize: '1.125rem',
    },
  }),
);

interface Props {
  isAuthenticated: boolean;
  hasKnownOutage?: boolean;
  outageCount?: number;
  closeBtnVisible?: boolean;
  onHandleClose?: () => void;
  title?: string;
  showHomeIcon?: boolean;
  showOutageCount?: boolean;
}

export const OutageMapPanelHeader = (props: Props) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const { showHomeIcon } = props;
  const {
    isAuthenticated,
    hasKnownOutage,
    outageCount,
    closeBtnVisible,
    onHandleClose,
    title,
    showOutageCount = true,
  } = props;
  const panelHeader = title
    ? title
    : isAuthenticated
    ? t('MY_ACCOUNT')
    : t('PGE_OUTAGE_MAP');

  return (
    <Box
      className={clsx(
        classes.container,
        !showHomeIcon && !closeBtnVisible ? classes.centerAlign : '',
      )}
      data-testid="OutageMapPanelHeader"
    >
      <Box
        className={clsx(isAuthenticated && classes.homeIcon, {
          [classes.isOut]: hasKnownOutage,
          [classes.isNotOut]: !closeBtnVisible && !hasKnownOutage,
          [classes.noOutagePin]:
            !hasKnownOutage && closeBtnVisible && !showHomeIcon,
          [classes.noOutageHome]:
            !hasKnownOutage && closeBtnVisible && showHomeIcon,
        })}
      >
        {!showHomeIcon && closeBtnVisible && (
          <OutageCountCircleIcon
            outageCount={
              showOutageCount
                ? outageCount && outageCount !== -1
                  ? outageCount
                  : 0
                : null
            }
            data-testid="OMPOutageCircleIcon"
          />
        )}

        {((showHomeIcon && outageCount !== -1 && !Boolean(outageCount)) ||
          (showHomeIcon && isAuthenticated)) && <HomeIcon />}
      </Box>
      <Box>
        <Typography
          className={classes.outageLeftPanelHeader}
          variant="h1"
          data-testid="OMPTitle"
        >
          <strong>{panelHeader}</strong>
        </Typography>
      </Box>
      {closeBtnVisible && (
        <Box className={classes.closeIcon}>
          <IconButton className={classes.closeIconBtn} onClick={onHandleClose}>
            <CloseIcon />
          </IconButton>
        </Box>
      )}
    </Box>
  );
};

export default OutageMapPanelHeader;
